import React from 'react';
import clsx from 'clsx';
import { Portal, CloseButton, ActionIcon, Divider } from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconArrowsMaximize,
  IconArrowsMinimize,
} from '@tabler/icons-react';
import { useSwipeable } from 'react-swipeable';
import { useTranslation } from 'react-i18next';
import { useDeviceLayout } from '../../../hooks';
import { useResizablePanel } from '../../../hooks/useResizablePanel';
import { useGeoMarkupStore } from '../../../store';
import { TopBar, TopBarActions } from '../../layout/AppShell/TopBar';
import { ResizeHandle } from '../ResizeHandle';
import { SwipeHintTooltip } from '../SwipeHintTooltip';
import {
  SLIDE_PANEL_ANIMATION_DURATION,
  SLIDE_PANEL_MIN_WIDTH,
  SWIPE_HINT_AUTO_HIDE_DURATION,
  PANEL_WIDTH_KEY,
  ICON_SIZE_LG,
} from '../../../constants/ui';
import type { SlidePanelProps } from './SlidePanel.types';
import { CollapsedFormActions } from './CollapsedFormActions';
import styles from './SlidePanel.module.css';

export const SlidePanel: React.FC<SlidePanelProps> = ({
  opened,
  onClose,
  headerContent,
  headerTitle,
  toolbarActions,
  mergedToolbar = false,
  children,
  footerContent,
  showFooter,
  width: defaultWidth = 480,
  withCloseButton = true,
  className = '',
  closeButtonAriaLabel,
  isHidden = false,
  translationPrefix = 'ui.slidePanel',
  allowMinimize = true,
  disableMobileAnimations = false,
  previewMode = false,
  onPreviewModeChange,
  collapsedActions,
  onEdit,
  onDelete,
  withExpandButton = true,
  withResizeHandle = true,
  fullscreenTopBar,
  variant = 'floating',
  onFullscreenChange,
  // Applied to the fullscreen store on every open transition (false→true).
  // `undefined` preserves the user's last Expand/Collapse choice.
  defaultFullscreen,
  fullscreenWideContent = false,
  mobileBelowSidePanel = false,
  ref,
}) => {
  const { t } = useTranslation();
  const { isMobile, isTouch: isTouchPrimary } = useDeviceLayout();
  const isDocked = !isMobile && variant === 'docked';
  const setIsAnyPanelResizing = useGeoMarkupStore(
    (s) => s.ui.setIsAnyPanelResizing
  );
  const [shouldRender, setShouldRender] = React.useState(opened);
  const [isEntering, setIsEntering] = React.useState(false);
  const [isPreEnteringMobile, setIsPreEnteringMobile] = React.useState(opened);
  // Desktop counterpart of isPreEnteringMobile: keeps the panel off-screen on the
  // first frame so the slide-in keyframe doesn't flash at the end position.
  // Initialised to `true` (not `opened`) because the panel is usually mounted
  // closed and toggled open — the flag must already be set when shouldRender
  // first flips to true, otherwise the very first open would still flash.
  const [isPreEnteringDesktop, setIsPreEnteringDesktop] = React.useState(true);
  const [isExiting, setIsExiting] = React.useState(false);
  // Fullscreen state lives in the store so view-switch remounts don't lose
  // the user's Expand/Collapse choice — panel is fullscreen or docked across
  // map ↔ list/kanban switches until the user explicitly toggles it.
  const isFullscreen = useGeoMarkupStore((s) => s.ui.slidePanel.isFullscreen);
  const setSlidePanelFullscreen = useGeoMarkupStore(
    (s) => s.ui.setSlidePanelFullscreen
  );

  React.useImperativeHandle(
    ref,
    () => ({
      enterFullscreen: () => setSlidePanelFullscreen(true),
      exitFullscreen: () => setSlidePanelFullscreen(false),
    }),
    [setSlidePanelFullscreen]
  );

  // On open transition (false→true): if `defaultFullscreen` is defined, reset
  // the store to that value (consumers that want a fixed initial mode).
  // If `undefined`, preserve the user's last Expand/Collapse choice across
  // open/close cycles.
  //
  // The ref is seeded with the MOUNT-TIME `opened` (not `false`) so a panel
  // that REMOUNTS while already open is not mistaken for a fresh open. This
  // happens on a map ↔ list/kanban view switch: the SlidePanel variant flips
  // floating↔docked and the panel remounts — seeding `false` would re-run the
  // reset and clobber the user's Expand/Collapse choice (e.g. a detail panel
  // with `defaultFullscreen: true` snapping back to fullscreen). Genuine opens
  // still reset: the panel mounts closed (store starts closed; the URL→store
  // sync opens it post-mount), so the ref seeds `false` and false→true fires.
  const prevOpenedRef = React.useRef(opened);
  React.useEffect(() => {
    if (!prevOpenedRef.current && opened && defaultFullscreen !== undefined) {
      setSlidePanelFullscreen(defaultFullscreen);
    }
    prevOpenedRef.current = opened;
  }, [opened, defaultFullscreen, setSlidePanelFullscreen]);

  // Single source of `onFullscreenChange` emission — any store write
  // (toolbar button, imperative handle) reaches the consumer through here,
  // including the initial value on mount. Ref dedupes same-value writes.
  const lastEmittedFullscreenRef = React.useRef<boolean | null>(null);
  React.useLayoutEffect(() => {
    if (!opened) {
      lastEmittedFullscreenRef.current = null;
      return;
    }
    if (lastEmittedFullscreenRef.current === isFullscreen) return;
    lastEmittedFullscreenRef.current = isFullscreen;
    onFullscreenChange?.(isFullscreen);
  }, [opened, isFullscreen, onFullscreenChange]);

  const storeSidePanelOpen = useGeoMarkupStore((s) => s.ui.sidePanel.isOpen);
  // Only the floating (map) variant has a separate left SidePanel overlay to
  // respect — in docked (list/kanban) views the artifact list is the page
  // itself, so reserving 380 px would just expose the underlying view.
  const isSidePanelOpen =
    isFullscreen && !isMobile && variant === 'floating' && storeSidePanelOpen;

  // Mobile swipe-to-minimize state (Zustand)
  const isMinimized = useGeoMarkupStore((s) => s.ui.slidePanel.isMinimized);
  const setMobilePanelMinimized = useGeoMarkupStore(
    (s) => s.ui.setMobilePanelMinimized
  );
  const swipeDownHintShown = useGeoMarkupStore(
    (s) => s.ui.slidePanel.swipeDownHintShown
  );
  const swipeUpHintShown = useGeoMarkupStore(
    (s) => s.ui.slidePanel.swipeUpHintShown
  );
  const dismissSwipeDownHint = useGeoMarkupStore(
    (s) => s.ui.dismissSwipeDownHint
  );
  const dismissSwipeUpHint = useGeoMarkupStore((s) => s.ui.dismissSwipeUpHint);

  // SlidePanel self-registration for generic toolbar repositioning
  const registerSlidePanel = useGeoMarkupStore((s) => s.ui.registerSlidePanel);
  const unregisterSlidePanel = useGeoMarkupStore(
    (s) => s.ui.unregisterSlidePanel
  );
  const setPanelWidth = useGeoMarkupStore((s) => s.ui.setPanelWidth);

  // useLayoutEffect so openSlidePanelCount is incremented before the browser
  // paints — downstream mobile selectors (selectShouldHideUIOnMobile) hide
  // chrome on the same frame the panel appears, not one frame later.
  React.useLayoutEffect(() => {
    if (opened) {
      registerSlidePanel();
      return () => unregisterSlidePanel();
    }
    return undefined;
  }, [opened, registerSlidePanel, unregisterSlidePanel]);

  // Hide panel only when toolbar guide is open on mobile
  const isGuideOpen = useGeoMarkupStore((s) => s.ui.toolbarGuide.isOpen);

  // Local state for fade-out animation (swipe hints)
  const [isFadingOut, setIsFadingOut] = React.useState(false);

  // Local state for swipe-to-close animation (panels with allowMinimize=false)
  const [isClosingViaSwipe, setIsClosingViaSwipe] = React.useState(false);

  // Track if panel is expanding from minimized/preview state - blocks enter animation to prevent double animation
  // (CSS transition handles the expand animation, slideUp animation would cause "double expand" effect)
  const isExpandingPanel = React.useRef(false);

  // Hint visibility logic (shows immediately when panel opens in respective state)
  // Only show hints if minimization is allowed for this panel and guide is not open
  const showSwipeDownHint =
    allowMinimize &&
    !swipeDownHintShown &&
    !isMinimized &&
    opened &&
    !isGuideOpen;
  const showSwipeUpHint =
    allowMinimize && !swipeUpHintShown && isMinimized && opened && !isGuideOpen;

  // Stable callback for resize width sync (avoids handleMouseMove/listener recreation on every render)
  const handleWidthChange = React.useCallback(
    (newWidth: number) => setPanelWidth(newWidth),
    [setPanelWidth]
  );

  // Desktop resizable panel logic (disabled for touch devices)
  const { width, isResizing, handleMouseDown } = useResizablePanel({
    defaultWidth,
    minWidth: SLIDE_PANEL_MIN_WIDTH,
    maxWidthPercent: 40,
    storageKey: PANEL_WIDTH_KEY,
    opened,
    onWidthChange: handleWidthChange,
    disabled: isTouchPrimary, // Disable resize for touch-primary devices
  });

  // Determine if footer should be shown
  const shouldShowFooter =
    showFooter !== undefined ? showFooter : !!footerContent;

  // Header content row. On mobile the form title (headerTitle) falls back here
  // since the mobile toolbar has no free left slot; on desktop the title lives
  // in the toolbar row and only headerContent (detail panels) uses this row.
  const headerRowContent = isMobile
    ? (headerContent ?? headerTitle)
    : headerContent;

  /**
   * Swipe handlers for mobile minimize/restore/preview functionality
   * Only active on mobile devices
   */
  const swipeHandlers = useSwipeable({
    onSwipedDown: () => {
      if (!isMobile) return;

      // In preview mode: swipe down minimizes the panel
      if (previewMode && allowMinimize) {
        setMobilePanelMinimized(true);
        onPreviewModeChange?.(false); // Exit preview mode when minimizing
        dismissSwipeDownHint();
        return;
      }

      if (allowMinimize) {
        // Panels WITH minimize (TaskForm, TaskDetails): minimize on swipe down
        if (isMinimized) return;
        setMobilePanelMinimized(true);
        dismissSwipeDownHint();
      } else {
        // Panels WITHOUT minimize (GeometriesList): animate then close
        setIsClosingViaSwipe(true);
      }
    },
    onSwipedUp: () => {
      if (!isMobile) return;

      // In preview mode: swipe up expands to full view
      if (previewMode) {
        // Mark as expanding to prevent enter animation (CSS transition handles the animation)
        isExpandingPanel.current = true;
        onPreviewModeChange?.(false);
        return;
      }

      // From minimized: restore panel
      if (isMinimized) {
        // Mark as expanding to prevent enter animation (CSS transition handles the animation)
        isExpandingPanel.current = true;
        setMobilePanelMinimized(false);
        // Always dismiss hint when user performs the swipe action (persisted via Zustand)
        dismissSwipeUpHint();
      }
    },
    delta: 50, // Minimum swipe distance (50px)
    trackMouse: false, // Touch only, no mouse tracking
    preventScrollOnSwipe: true, // Prioritize swipe over scroll for minimize/close gesture
  });

  // Synchronize isResizing state with Zustand (for DrawingInstructions tooltip transition)
  React.useEffect(() => {
    if (!isMobile) {
      setIsAnyPanelResizing(isResizing);
    }
  }, [isResizing, isMobile, setIsAnyPanelResizing]);

  // Sync panel width to Zustand when panel opens/mounts (not just on resize)
  // This ensures DrawingInstructions tooltip has correct panel width for position calculation
  // NOTE: 'width' intentionally excluded from dependencies to prevent duplicate updates during resize
  // Width changes are synced via useResizablePanel callbacks (drag, reset, window resize)
  React.useEffect(() => {
    if (opened && !isMobile) {
      setPanelWidth(width);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isMobile]);

  // Handle mounting/unmounting with enter/exit animations
  React.useEffect(() => {
    let enterAnimationTimer: ReturnType<typeof setTimeout> | null = null;
    let exitTimer: ReturnType<typeof setTimeout> | null = null;
    let rafOuter: number | null = null;
    let rafInner: number | null = null;

    if (opened) {
      setShouldRender(true);
      setIsExiting(false);

      const shouldSkipAnimation = isMobile && disableMobileAnimations;

      if (isMobile) {
        if (shouldSkipAnimation || isExpandingPanel.current) {
          setIsPreEnteringMobile(false);
        } else {
          // Double-RAF: single RAF can be coalesced with the state commit and
          // so the off-screen frame would never paint before the class drops.
          rafOuter = requestAnimationFrame(() => {
            rafInner = requestAnimationFrame(() => {
              setIsPreEnteringMobile(false);
            });
          });
        }
      } else if (!isEntering && !shouldSkipAnimation) {
        // Double-RAF mirrors the mobile path: the panel paints off-screen via
        // .preEnterDesktop on the first frame, then the class is swapped for
        // .enteringDesktop so the slide-in keyframe never flashes at the end
        // position (a single RAF can be coalesced with the state commit).
        rafOuter = requestAnimationFrame(() => {
          rafInner = requestAnimationFrame(() => {
            setIsPreEnteringDesktop(false);
            setIsEntering(true);
            enterAnimationTimer = setTimeout(() => {
              setIsEntering(false);
            }, SLIDE_PANEL_ANIMATION_DURATION);
          });
        });
      }
    } else if (!opened && shouldRender) {
      // Fullscreen store value is intentionally NOT reset on close — the
      // user's last Expand/Collapse choice persists across panel open/close
      // cycles (and through view switches) until they toggle it.
      setIsEntering(false);
      // Re-arm the desktop pre-enter NOW (not after the exit completes): the
      // render-time guard (`!isExiting`) keeps the off-screen class from
      // applying while the panel animates out, but a reopen that interrupts the
      // exit (< animation duration) then already has the flag set and won't
      // flash at the end position. Safe on mobile (the desktop flag is unused).
      if (!isMobile) setIsPreEnteringDesktop(true);

      if (isMobile && (isMinimized || disableMobileAnimations)) {
        setShouldRender(false);
        setIsPreEnteringMobile(true);
      } else {
        rafOuter = requestAnimationFrame(() => {
          setIsExiting(true);
          exitTimer = setTimeout(() => {
            rafInner = requestAnimationFrame(() => {
              setShouldRender(false);
              if (isMobile) setIsPreEnteringMobile(true);
            });
          }, SLIDE_PANEL_ANIMATION_DURATION);
        });
      }
    }

    return () => {
      if (rafOuter !== null) cancelAnimationFrame(rafOuter);
      if (rafInner !== null) cancelAnimationFrame(rafInner);
      if (enterAnimationTimer !== null) clearTimeout(enterAnimationTimer);
      if (exitTimer !== null) clearTimeout(exitTimer);
    };
    // Note: isMinimized intentionally excluded from deps - it only controls CSS class, not lifecycle
    // Including it would cause unwanted re-renders when minimize state changes
    // Note: isEntering intentionally excluded - we read it as guard but cannot include in deps
    // (would cause infinite loop since we set it inside the effect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, shouldRender, isMobile]);

  // Reset the "expanding from minimize/preview" flag once the panel has
  // settled into the full state — its only purpose was to suppress the enter
  // animation during the swipe-up transition, and the next open cycle must
  // start with a clean ref.
  React.useEffect(() => {
    if (!isMinimized && !previewMode) {
      isExpandingPanel.current = false;
    }
  }, [isMinimized, previewMode]);

  // Handle escape key
  React.useEffect(() => {
    if (!opened) return;

    const handleEscape = (event: KeyboardEvent) => {
      // Only close if ESC wasn't already handled by child components (e.g., ControlsPanel, SettingsPanel)
      if (event.key === 'Escape' && !event.defaultPrevented) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, opened]);

  // Body scroll lock: active whenever the panel covers the content area.
  // Docked list/kanban keeps the page scrollable; fullscreen always locks
  // regardless of variant (it overlays the whole workspace).
  React.useEffect(() => {
    if (opened && (!isDocked || isFullscreen)) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [opened, isDocked, isFullscreen]);

  // Swipe-to-close animation for panels without minimize (allowMinimize=false)
  // After animation completes, call onClose to update state
  React.useEffect(() => {
    if (!isClosingViaSwipe) return;

    const timer = setTimeout(() => {
      onClose();
      setIsClosingViaSwipe(false);
    }, 300); // Match CSS transition duration (0.3s)

    return () => clearTimeout(timer);
  }, [isClosingViaSwipe, onClose]);

  // Auto-hide swipe hints after timeout (8s) with fade-out animation
  React.useEffect(() => {
    if (!showSwipeDownHint && !showSwipeUpHint) return;

    // Start fade-out animation 500ms before hiding (7.5s)
    const fadeOutTimer = setTimeout(() => {
      setIsFadingOut(true);
    }, SWIPE_HINT_AUTO_HIDE_DURATION - 500);

    // Hide tooltip after full duration (8s)
    const hideTimer = setTimeout(() => {
      if (showSwipeDownHint) {
        dismissSwipeDownHint();
      }
      if (showSwipeUpHint) {
        dismissSwipeUpHint();
      }
      setIsFadingOut(false); // Reset fade-out state
    }, SWIPE_HINT_AUTO_HIDE_DURATION);

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(hideTimer);
    };
  }, [
    showSwipeDownHint,
    showSwipeUpHint,
    dismissSwipeDownHint,
    dismissSwipeUpHint,
  ]);

  // Early return after hooks
  // Hide panel on mobile when toolbar guide is open (provide clean onboarding experience)
  if (isMobile && isGuideOpen) {
    return null;
  }

  if (!shouldRender) {
    return null;
  }

  const containerClass = isMobile
    ? styles.containerMobile
    : styles.containerDesktop;
  // Skip the off-screen pre-enter frame when expanding from minimized/preview
  // (the state-class transform transition is itself the entrance animation)
  // or when animations are disabled by the consumer.
  const shouldSkipMobileEnter =
    isMobile && (isExpandingPanel.current || disableMobileAnimations);
  let enterClass = '';
  if (!isExiting) {
    if (isMobile && isPreEnteringMobile && !shouldSkipMobileEnter) {
      enterClass = styles.preEnterMobile ?? '';
    } else if (!isMobile && isPreEnteringDesktop) {
      enterClass = styles.preEnterDesktop ?? '';
    } else if (!isMobile && isEntering) {
      enterClass = styles.enteringDesktop ?? '';
    }
  }

  // Skip exit animation on mobile if disableMobileAnimations is true (instant close)
  const exitClass =
    isExiting && !(isMobile && disableMobileAnimations)
      ? isMobile
        ? styles.exitingMobile
        : styles.exitingDesktop
      : '';
  // Apply minimized class for: 1) panels with minimize that are minimized, 2) panels closing via swipe
  // Use minimizedWithActions for taller bar when collapsed form actions are present
  const minimizedClass =
    isMobile && ((allowMinimize && isMinimized) || isClosingViaSwipe)
      ? collapsedActions
        ? styles.minimizedWithActions
        : styles.minimized
      : '';
  // Apply preview mode class for partial expansion on mobile
  const previewModeClass =
    isMobile && previewMode && !isMinimized ? styles.previewMode : '';
  // Optional visibility control (caller determines when to hide panel)
  // Ignore isHidden during BOTH enter and exit animations - prevents blocking slide-in/slide-out
  const shouldApplyHidden = isHidden && !isEntering && !isExiting;
  const hiddenClass = shouldApplyHidden ? styles.hidden : '';
  const isDesktopFullscreen = !isMobile && isFullscreen;
  // Merged-toolbar panels (transparent toolbar folded into the themed surface)
  // suppress the fullscreen TopBar entirely so the panel reads as one bar.
  const effectiveFullscreenTopBar = isDesktopFullscreen
    ? mergedToolbar
      ? null
      : (fullscreenTopBar ??
        (variant === 'floating' ? (
          <TopBar hideLogo actions={<TopBarActions variant="compact" />} />
        ) : undefined))
    : undefined;

  return (
    <Portal>
      {/* Mobile overlay (backdrop) - REMOVED to allow MapToolbox visibility on mobile */}
      {/* The panel is still modal and has proper focus management without backdrop */}

      {/* Main container */}
      <div
        className={clsx(
          containerClass,
          className,
          enterClass,
          exitClass,
          minimizedClass,
          previewModeClass,
          hiddenClass,
          isDocked && styles.containerDocked,
          isDesktopFullscreen && styles.fullscreen,
          isDesktopFullscreen &&
            !effectiveFullscreenTopBar &&
            !mergedToolbar &&
            styles.fullscreenInset,
          isDesktopFullscreen && isSidePanelOpen && styles.withSidePanel,
          isDesktopFullscreen && fullscreenWideContent && styles.fullscreenWide,
          isMobile && mobileBelowSidePanel && styles.belowSidePanel,
          isResizing && styles.resizing
        )}
        style={
          !isMobile
            ? ({ '--container-width': `${width}px` } as React.CSSProperties)
            : undefined
        }
        role="dialog"
        aria-modal="true"
      >
        {/* Desktop resize handle (hidden for mobile, touch-primary devices, and fullscreen) */}
        {withResizeHandle && !isMobile && !isTouchPrimary && !isFullscreen && (
          <ResizeHandle onMouseDown={handleMouseDown} isResizing={isResizing} />
        )}

        {/* Mobile: swipe zone with toolbar (drag indicator embedded in toolbar) */}
        {isMobile && (
          <div
            {...swipeHandlers}
            className={`${styles.dragIndicatorWrapper} ${withCloseButton ? styles.withToolbar : ''}`}
          >
            {/* Standalone drag indicator — visible when minimized (toolbar hidden) */}
            <div
              className={styles.dragIndicator}
              aria-label={t(`${translationPrefix}.mobile.dragIndicator`)}
            />
            {/* Toolbar with embedded drag indicator — visible when expanded */}
            {withCloseButton && (
              <div className={styles.headerToolbar}>
                <div className={styles.dragIndicatorRow}>
                  <div
                    className={styles.dragIndicator}
                    aria-label={t(`${translationPrefix}.mobile.dragIndicator`)}
                  />
                </div>
                <div className={styles.toolbarLeft}>
                  {onEdit && (
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      onClick={onEdit}
                      aria-label={t('ui.slidePanel.toolbar.edit')}
                    >
                      <IconEdit size={ICON_SIZE_LG} />
                    </ActionIcon>
                  )}
                  {onDelete && (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="lg"
                      onClick={onDelete}
                      aria-label={t('ui.slidePanel.toolbar.delete')}
                    >
                      <IconTrash size={ICON_SIZE_LG} />
                    </ActionIcon>
                  )}
                </div>
                <div className={styles.toolbarRight}>
                  <CloseButton
                    onClick={onClose}
                    aria-label={
                      closeButtonAriaLabel || t('ui.slidePanel.toolbar.close')
                    }
                    size="lg"
                  />
                </div>
              </div>
            )}
            <SwipeHintTooltip
              visible={showSwipeDownHint && !isMinimized}
              fadingOut={isFadingOut}
              position="absolute"
              className={styles.swipeHintPosition}
            >
              {t(`${translationPrefix}.mobile.swipeDownHint`)}
            </SwipeHintTooltip>
            <SwipeHintTooltip
              visible={showSwipeUpHint && isMinimized}
              fadingOut={isFadingOut}
              position="absolute"
              className={styles.swipeHintPosition}
            >
              {t(`${translationPrefix}.mobile.swipeUpHint`)}
            </SwipeHintTooltip>
            {/* Collapsed form actions (mobile only, when minimized) */}
            {isMinimized && collapsedActions && (
              <CollapsedFormActions {...collapsedActions} />
            )}
          </div>
        )}

        {/* Desktop fullscreen: custom or default top bar */}
        {isDesktopFullscreen && effectiveFullscreenTopBar}

        {/* Desktop: standalone toolbar */}
        {!isMobile && withCloseButton && (
          <div
            className={clsx(
              styles.headerToolbar,
              mergedToolbar &&
                isDesktopFullscreen &&
                styles.headerToolbarTransparent
            )}
          >
            {headerTitle && (
              <div className={styles.toolbarTitle}>{headerTitle}</div>
            )}
            <div className={styles.toolbarRight}>
              {toolbarActions && (
                <>
                  {toolbarActions}
                  <Divider
                    orientation="vertical"
                    className={styles.toolbarSeparator}
                  />
                </>
              )}
              {onEdit && (
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={onEdit}
                  aria-label={t('ui.slidePanel.toolbar.edit')}
                >
                  <IconEdit size={ICON_SIZE_LG} />
                </ActionIcon>
              )}
              {onDelete && (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  onClick={onDelete}
                  aria-label={t('ui.slidePanel.toolbar.delete')}
                >
                  <IconTrash size={ICON_SIZE_LG} />
                </ActionIcon>
              )}
              {(onEdit || onDelete) && (
                <Divider
                  orientation="vertical"
                  className={styles.toolbarSeparator}
                />
              )}
              {withExpandButton && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  onClick={() => setSlidePanelFullscreen(!isFullscreen)}
                  aria-label={t(
                    isFullscreen
                      ? 'ui.slidePanel.toolbar.collapse'
                      : 'ui.slidePanel.toolbar.expand'
                  )}
                >
                  {isFullscreen ? (
                    <IconArrowsMinimize size={ICON_SIZE_LG} />
                  ) : (
                    <IconArrowsMaximize size={ICON_SIZE_LG} />
                  )}
                </ActionIcon>
              )}
              <CloseButton
                onClick={onClose}
                aria-label={
                  closeButtonAriaLabel || t('ui.slidePanel.toolbar.close')
                }
                size="lg"
              />
            </div>
          </div>
        )}

        {/* Header Content — title, step counter, etc. (see headerRowContent) */}
        {headerRowContent && (
          <div className={styles.header}>
            <div className={styles.headerContent}>{headerRowContent}</div>
          </div>
        )}

        {/* Content — when no header row renders (desktop title-in-toolbar
            panels), restore the top spacing the header row used to provide. */}
        <div
          className={clsx(
            styles.content,
            !headerRowContent && styles.contentNoHeaderRow,
            mergedToolbar && isDesktopFullscreen && styles.contentFlush
          )}
        >
          {children}
        </div>

        {/* Footer (optional) */}
        {shouldShowFooter && footerContent && (
          <div className={styles.footer}>{footerContent}</div>
        )}
      </div>
    </Portal>
  );
};
